import { useState } from "react";
import { Link, useParams } from "wouter";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { BellOff, Mail, ToggleLeft, ToggleRight } from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer";
import { Button } from "../components/ui/button";

/** One preference category as returned by `userNotifications.getPrefsByToken`. */
type CategoryRow = {
  key: "submissionUpdates" | "studioInvites" | "venueUpdates";
  enabled: boolean;
  label: string;
  description: string;
};

/**
 * Reached only from the tokenised "Manage email preferences" link in a
 * recipient-facing email, so it must work with no account and no Clerk — it is
 * registered in both AuthRoutes and NoAuthRoutes. The token in the URL is the
 * credential, exactly as on ManageRemindersPage.
 */
export function ManageNotificationsPage() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";

  const data = useQuery(
    api.userNotifications.getPrefsByToken,
    token ? { token } : "skip"
  );
  const setPref = useMutation(api.userNotifications.setPrefByToken);
  const unsubscribe = useMutation(api.userNotifications.unsubscribeByToken);
  const [busy, setBusy] = useState<string | null>(null);

  if (!token || data === null) {
    return (
      <PageContainer>
        <div className="max-w-lg mx-auto py-16 text-center">
          <BellOff className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-foreground mb-2">
            This link isn't valid
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            It may have already been used to unsubscribe, or the link was
            truncated by your email client.
          </p>
          <Button asChild>
            <Link href="/events">Browse events</Link>
          </Button>
        </div>
      </PageContainer>
    );
  }

  if (data === undefined) {
    return (
      <PageContainer>
        <div className="max-w-lg mx-auto py-12 space-y-3">
          <div className="h-8 w-48 rounded bg-muted animate-pulse" />
          <div className="h-20 rounded-lg bg-muted animate-pulse" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-lg mx-auto py-10">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Email preferences
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            For <span className="text-foreground">{data.email}</span>
          </p>
        </header>

        {data.unsubscribed ? (
          <div className="mb-6 p-4 rounded-lg border border-border bg-muted">
            <p className="text-sm text-foreground font-medium mb-1">
              You're unsubscribed
            </p>
            <p className="text-sm text-muted-foreground">
              We won't email this address again. Submitting something new won't
              start it up either — you'd need to ask us to remove the block.
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {(data.categories as CategoryRow[]).map(
              ({ key, enabled, label, description }) => (
                <li
                  key={key}
                  className="flex items-start justify-between gap-4 p-4 rounded-lg border border-border bg-card"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">{label}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {description}
                    </p>
                  </div>
                  <button
                    disabled={busy === key}
                    onClick={async () => {
                      setBusy(key);
                      try {
                        await setPref({ token, category: key, enabled: !enabled });
                      } finally {
                        setBusy(null);
                      }
                    }}
                    className="shrink-0 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    title={`${label}: ${enabled ? "on" : "off"}`}
                    aria-pressed={enabled}
                  >
                    {enabled ? (
                      <ToggleRight className="w-6 h-6 text-primary" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-muted-foreground" />
                    )}
                    <span className="w-6 text-left">
                      {enabled ? "On" : "Off"}
                    </span>
                  </button>
                </li>
              )
            )}
          </ul>
        )}

        <div className="mt-8 pt-6 border-t border-border">
          <p className="text-sm text-muted-foreground mb-3">
            Turning a category off stops that kind of email only. Event
            reminders you set yourself are managed from the link in any reminder
            email.
          </p>
          {!data.unsubscribed && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy === "all"}
              onClick={async () => {
                setBusy("all");
                try {
                  await unsubscribe({ token });
                } finally {
                  setBusy(null);
                }
              }}
              className="text-muted-foreground hover:text-destructive"
            >
              <BellOff className="w-4 h-4" />
              {busy === "all"
                ? "Unsubscribing..."
                : "Unsubscribe from all email"}
            </Button>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
